import { FunctionComponent, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "../ui/dialog";
import { Exercise, Workout } from "./workoutsColumns";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { errorToast } from "@/lib/utils/ToastUtils";
import { toast } from "sonner";
import { store } from "@/lib/states/store";
import LoadingSpinner from "../LoadingSpinner";
import { trpc } from "@/lib/trpc/client";

interface SaveToLibraryDialogProps {
  workout: Workout | undefined;
  onClose: () => void;
}

const SaveToLibraryDialog: FunctionComponent<SaveToLibraryDialogProps> = ({
  workout,
  onClose,
}) => {
  const toNullableNumber = (value: unknown): number | null => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return null;
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const saveWorkoutMutation = trpc.workouts.save.useMutation();
  const utils = trpc.useUtils();

  useEffect(() => {
    setName(workout?.name ?? "");
  }, [workout]);

  const saveWorkout = async () => {
    const orgId = store.getState().session.org?.id;

    if (!workout || !orgId) {
      return;
    }

    setLoading(true);

    let sortedExercises: Exercise[] = [];
    // Combine nested exercise collection into one array
    workout.exerciseCollection.forEach((ex, order) => {
      const exercises = ex.map((exercise, subOrder) => {
        return {
          id: exercise.id,
          sets: exercise.sets,
          tempo: exercise.tempo ?? "",
          rir: toNullableNumber(exercise.rir),
          rest_time: toNullableNumber(exercise.rest_time),
          coach_note: exercise.coach_note ?? null,
          ex_order: order,
          sub_order: subOrder,
        } as Exercise;
      });
      sortedExercises = sortedExercises.concat(exercises);
    });

    try {
      const data = await saveWorkoutMutation.mutateAsync({
        payload: {
          workout: {
            org_id: orgId,
            name,
            description: workout.description ?? "",
            is_template: true,
            exercises: sortedExercises.map((exercise) => ({
              exercise_id: exercise.id,
              sets: exercise.sets,
              tempo: exercise.tempo ?? "",
              rir: exercise.rir,
              rest_time: exercise.rest_time,
              coach_note: exercise.coach_note ?? null,
              ex_order: exercise.ex_order,
              sub_order: exercise.sub_order,
              position: exercise.ex_order,
              superset_key:
                workout.exerciseCollection[exercise.ex_order]?.length > 1
                  ? `S${exercise.ex_order + 1}`
                  : null,
              superset_position: exercise.sub_order,
            })),
          },
        },
      });

      const savedWorkout =
        data && typeof data === "object" && "workout" in data
          ? (data.workout as { id?: string; created_at?: string } | undefined)
          : null;

      if (savedWorkout?.id) {
        await utils.workouts.list.invalidate();
      }
    } catch (error) {
      console.error("error adding workout", error);
      errorToast(error instanceof Error ? error : null);
      setLoading(false);
      return;
    }

    setLoading(false);
    toast.success("Workout saved to library");
    onClose();
  };

  return (
    <Dialog
      open={workout !== undefined}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      analyticsName="save_to_library_dialog"
    >
      <DialogContent className="flex flex-col">
        <DialogHeader>Save To Library</DialogHeader>

        <Label>Workout Name</Label>
        <Input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <DialogFooter>
          <Button onClick={onClose} variant={"ghost"}>
            Cancel
          </Button>
          <Button onClick={saveWorkout} disabled={loading}>
            {loading ? <LoadingSpinner /> : "Save To Library"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveToLibraryDialog;
